import { Injectable, inject } from "@angular/core";
import { EMPTY, Observable, filter, map, merge } from "rxjs";
import { PLATFORM_ADAPTER } from "../core/platform/platform.adapter";
import { DatabaseService } from "../services/database/database.service";
import { NativeSyncService } from "./native-sync.service";
import { TableChangeNotifierService } from "./table-change-notifier.service";
import { SyncTable } from "./ws-envelope";

/**
 * Rows of a sync table changed, whatever wrote them (#962): another device,
 * another tab, or this page. A view that shows the data subscribes here and
 * reads it again.
 *
 * - Web: the app-server sends every write to every tab, the writer's own too.
 * - Native: a peer's write arrives through `applied$`, after it is written to
 *   SQLite. The server never sends a device its own write back, so
 *   `localWrite$` adds this device's writes. The raw REMOTE stream is left out
 *   on native: it fires before the write, and on every reconnect the backfill
 *   resends the recent flights whether or not they changed.
 */
@Injectable({ providedIn: "root" })
export class SyncChangesService {
  private web = inject(TableChangeNotifierService);
  private native = inject(NativeSyncService);
  private db = inject(DatabaseService);
  private isNative = inject(PLATFORM_ADAPTER).isNative;

  /** Emits the changed table, for the given tables only. */
  changes(...tables: SyncTable[]): Observable<SyncTable> {
    return this.stream(false, tables);
  }

  /**
   * As `changes`, without the echo of a write that this window sent while its
   * calculation chain ran. For the one listener that the chain serves itself
   * when it ends. Web only: native has no such echo to tell apart.
   */
  changesNotOwnChain(...tables: SyncTable[]): Observable<SyncTable> {
    return this.stream(true, tables);
  }

  private stream(skipOwnChain: boolean, tables: SyncTable[]): Observable<SyncTable> {
    return merge(
      this.isNative
        ? EMPTY
        : this.web.onAnyChange().pipe(
            filter((c) => !(skipOwnChain && c.ownChain)),
            map((c) => c.table)
          ),
      this.native.applied$.pipe(map((c) => c.table)),
      this.db.localWrite$
    ).pipe(filter((t) => tables.includes(t)));
  }
}
